import { atom } from 'jotai'
import type { Atom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import isEqual from 'react-fast-compare'
import { viewerState$ } from '~/features/viewer/query'
import type { PlanId, RestaurantId } from '~/lib/graphql/scalars'
import * as Guest from '../guest'
import type { FavoriteState } from '../types'
import * as Member from './member'

type Key = { restaurantId: RestaurantId; planId: PlanId }

/** 与えられたプランのお気に入り状態 */
export const favoriteState$ = atomFamily<Key, Atom<FavoriteState>>(
  (key) =>
    atom((get) => {
      const { isLoading, state } = get(viewerState$)
      if (isLoading) {
        return { type: 'loading' }
      }
      switch (state) {
        case 'member':
          const { data, isSuccess } = get(Member.isFavoritePlan$(key))
          return isSuccess ? { type: 'loaded', isFavorite: data } : { type: 'loading' }
        case 'guest':
          return { type: 'loaded', isFavorite: get(Guest.favoritePlanIds$).includes(key.planId) }
        default:
          throw new Error(`Unexpected viewer state: ${state as '__invalid__'}`)
      }
    }),
  isEqual
)

/** 与えられたプランのお気に入り状態を更新する toggle 関数と更新中を示す isPending を返す
 *  writable atom から set を取り出すという dirty な方法を取っている
 */
export const togglePlanFavorite$ = atom(null, (get, set, key: Key) => {
  const { isLoading, state: viewerState } = get(viewerState$)
  const state = get(favoriteState$(key))
  if (isLoading || state.type === 'loading') {
    return { toggle() {}, isPending: true }
  }
  switch (viewerState) {
    case 'member':
      const { mutate, isPending } = get(Member.favoritePlan$)
      const verb = state.isFavorite ? 'unfavorite' : 'favorite'
      return { toggle: () => mutate({ verb, ...key }), isPending }
    case 'guest':
      const verb$ = state.isFavorite ? Guest.removePlanFromFavorites$ : Guest.addPlanToFavorites$
      return {
        toggle: () => set(verb$, key.planId),
        isPending: false,
      }
    default:
      throw new Error(`Unexpected viewer state: ${viewerState as '__invalid__'}`)
  }
})
